import { AlertTriangle, Banknote, Boxes, Megaphone, PackageCheck, UsersRound } from "lucide-react";
import Link from "next/link";
import { useEffect, useState } from "react";

import { AdminShell } from "@/components/admin/admin-shell";
import { AnalyticsDashboard } from "@/components/admin/analytics-dashboard";
import { apiGet } from "@/services/api";
import { formatPrice } from "@/lib/utils";

type Overview = {
  productCount: number;
  orderCount: number;
  userCount: number;
  promotionCount: number;
  revenue: number;
  dailyRevenue: Array<{ label: string; value: number }>;
  topProducts: Array<{ title: string; quantity: number }>;
};

/** `app/admin/page.tsx`. */
export function AdminOverviewPage() {
  const [data, setData] = useState<Overview | null>(null);
  const [dbError, setDbError] = useState(false);

  useEffect(() => {
    let active = true;
    void apiGet<Overview>("/api/admin/overview").then((payload) => {
      if (!active) return;
      if (payload) setData(payload);
      else setDbError(true);
    });
    return () => {
      active = false;
    };
  }, []);

  const productCount = data?.productCount ?? 0;
  const orderCount = data?.orderCount ?? 0;
  const userCount = data?.userCount ?? 0;
  const promotionCount = data?.promotionCount ?? 0;
  const revenue = data?.revenue ?? 0;
  const avgOrderValue = orderCount > 0 ? revenue / orderCount : 0;
  const dailyRevenue =
    data?.dailyRevenue ??
    Array.from({ length: 7 }, (_, i) => ({
      label: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][i],
      value: 0
    }));
  const topProducts = data?.topProducts ?? [];

  if (!data && !dbError) {
    return (
      <AdminShell title="Overview">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-32 animate-pulse rounded-lg bg-slate-100 dark:bg-white/5" />
          ))}
        </div>
      </AdminShell>
    );
  }

  return (
    <AdminShell title="Overview">
      <div className="space-y-6">
        {dbError && (
          <div className="flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 dark:border-amber-800/40 dark:bg-amber-950/30 dark:text-amber-200">
            <AlertTriangle className="h-5 w-5 shrink-0" />
            <p>
              Cannot reach the database right now. Stats below are unavailable — check your{" "}
              <code className="rounded bg-amber-100 px-1 dark:bg-amber-900">DATABASE_URL</code>{" "}
              environment variable.
            </p>
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          {[
            { label: "Products", value: productCount, icon: Boxes, href: "/admin/products" },
            { label: "Orders", value: orderCount, icon: PackageCheck, href: "/admin/orders" },
            { label: "Users", value: userCount, icon: UsersRound, href: "/admin/users" },
            { label: "Promotions & Banners", value: promotionCount, icon: Megaphone, href: "/admin/promotions" },
            { label: "Revenue (paid)", value: formatPrice(revenue), icon: Banknote, href: "/admin/orders" }
          ].map((stat) => {
            const Icon = stat.icon;
            return (
              <Link
                key={stat.label}
                href={stat.href}
                className="group block rounded-lg border border-slate-200 bg-white p-5 shadow-xs transition hover:border-amazon-orange hover:shadow-md dark:border-white/10 dark:bg-slate-900"
              >
                <div className="flex items-center justify-between">
                  <Icon className="h-6 w-6 text-amazon-orange transition group-hover:scale-110" />
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 group-hover:text-amazon-orange">
                    View &rarr;
                  </span>
                </div>
                <p className="mt-3 text-xs font-black uppercase text-slate-500">{stat.label}</p>
                <p className="mt-1 text-2xl sm:text-3xl font-black tracking-normal text-slate-950 dark:text-white">
                  {stat.value}
                </p>
              </Link>
            );
          })}
        </div>

        <AnalyticsDashboard
          revenue={revenue}
          orderCount={orderCount}
          userCount={userCount}
          avgOrderValue={avgOrderValue}
          dailyRevenue={dailyRevenue}
          topProducts={topProducts}
        />
      </div>
    </AdminShell>
  );
}